import type { PageData } from "../api";
import type { AppRoute } from "../routes";
import { DesignHeader, StatCards } from "../features/dashboard/pageChrome";
import { DataTable } from "../components/DataTable";
import { EmptyState } from "../components/EmptyState";
import {
  ActiveAlarmsPage,
  AlarmHistoryPage,
  AlarmMappingPage,
  AlarmTypeMappingPage,
  AlarmContactPage,
  AlarmRulesPage,
  AlarmWebhookPage,
  ApiMonitorPage,
  ControlActionLogPage,
  CsuDataPage,
  CsuMappingPage,
  CsuTemplatesEditor,
  DashboardDesign,
  DashboardMappingEditor,
  DatabaseSetupPage,
  DatabaseStatusDbPage,
  DatabaseStatusPage,
  DatabaseStructurePage,
  DownloadTasksPage,
  InventorySitePage,
  InventoryConfigPage,
  MapViewPage,
  MapConfigPage,
  AnalyticsPage,
  AnalyticsConfigPage,
  ConnectorPage,
  TaskStatusPage,
  CustomizedReportPage,
  FlowChatPage,
  SmtpSettingsPage,
  ReportTaskLogPage,
  DeviceUiConfigEditor,
  ModbusDeviceOpenPage,
  ModbusDevicesEditor,
  ModbusDeviceViewPage,
  ModbusGatewaysEditor,
  AtCommandPage,
  UartSettingPage,
  MqttConfigPage,
  ModbusPollDebugPage,
  ModbusTemplateDetailEditor,
  ModbusTemplatesEditor,
  OfflineAlarmRulesPage,
  PingTestPage,
  ProjectManagementPage,
  SnmpTesterPage,
  RedisStatusPage,
  SiteLogStatusPage,
  SiteOfflineHistoryPage,
  SitesEditor,
  SiteVariableLogPage,
  SnmpConfigPage,
  SnmpStatusPage,
  ModbusStatusPage,
  SystemSettingsPage,
  UsersCreatePage,
  UsersEditPage,
  UsersListPage,
  AccountActivityPage,
  WebhookLogPage,
  WebhookSenderPage,
} from "./asyncPages";
function GenericDesign({ route, page }: { route: AppRoute; page: PageData }) {
  return (
    <div className="space-y-5">
      <DesignHeader route={route} page={page} />
      {Object.keys(page.meta || {}).length > 0 && <StatCards page={page} />}
      {page.datasets.length ? (
        page.datasets.map((dataset) => (
          <section key={dataset.name} className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{dataset.label}</h3>
              <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                {dataset.count ?? dataset.rows.length} rows
              </span>
            </div>
            <DataTable dataset={dataset} />
          </section>
        ))
      ) : (
        <EmptyState title="React page shell is ready" description="This route is handled by React. Connect more JSON data to add richer controls." />
      )}
    </div>
  );
}
export function DesignedPage({ route, page }: { route: AppRoute; page: PageData }) {
  if (route.id === "map_view" || route.path === "/map") {
    return <MapViewPage route={route} page={page} />;
  }
  if (route.id === "inventory_site" || route.path === "/inventory/site") {
    return <InventorySitePage route={route} page={page} />;
  }
  if (route.id === "inventory_config" || route.path === "/inventory/config") {
    return <InventoryConfigPage route={route} page={page} />;
  }
  if (route.id === "map_config" || route.path === "/map-config") {
    return <MapConfigPage route={route} page={page} />;
  }
  if (route.id === "analytics" || route.path === "/analytics") {
    return <AnalyticsPage route={route} page={page} />;
  }
  if (route.id === "analytics_config" || route.path === "/analytics-config") {
    return <AnalyticsConfigPage route={route} page={page} />;
  }
  if (route.id === "connector" || route.path === "/connector") {
    return <ConnectorPage route={route} page={page} />;
  }
  if (route.id === "task_status" || route.path === "/task-status") {
    return <TaskStatusPage route={route} page={page} />;
  }
  if (route.id === "account_activity" || route.path === "/user-activity") {
    return <AccountActivityPage route={route} page={page} />;
  }
  if (route.id === "alarm_mapping") {
    return <AlarmMappingPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "alarm_type_mapping") {
    return (
      <AlarmTypeMappingPage
        route={route}
        page={page}
        onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))}
      />
    );
  }
  if (route.id === "alarm_contact" || route.path === "/alarm-contacts") {
    return <AlarmContactPage route={route} page={page} />;
  }
  if (route.id === "database_status") {
    return <DatabaseStatusPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "database_status_db") {
    return <DatabaseStatusDbPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "database_structure") {
    return <DatabaseStructurePage route={route} page={page} />;
  }
  if (route.id === "database_setup") {
    return <DatabaseSetupPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "settings") {
    return <SystemSettingsPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "users") {
    return <UsersListPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "users_new") {
    return <UsersCreatePage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "user_edit") {
    return <UsersEditPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "dashboard_mapping") {
    return <DashboardMappingEditor route={route} page={page} />;
  }
  if (route.id === "csu_data") {
    return <CsuDataPage route={route} page={page} />;
  }
  if (route.id === "csu_templates") {
    return <CsuTemplatesEditor route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "csu_template_detail") {
    return <CsuMappingPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "modbus_gateways") {
    return <ModbusGatewaysEditor route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "modbus_at_command") {
    return <AtCommandPage route={route} page={page} />;
  }
  if (route.id === "modbus_uart_setting") {
    return <UartSettingPage route={route} page={page} />;
  }
  if (route.id === "modbus_templates") {
    return <ModbusTemplatesEditor route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "modbus_template_detail") {
    return <ModbusTemplateDetailEditor route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "sites") {
    return <SitesEditor route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "modbus_devices") {
    return <ModbusDevicesEditor route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "modbus_device_view" || route.id === "modbus_device_view_machine") {
    return <ModbusDeviceViewPage route={route} page={page} />;
  }
  if (route.id === "modbus_device_poll_debug") {
    return <ModbusPollDebugPage route={route} page={page} />;
  }
  if (route.id === "modbus_device") {
    return <ModbusDeviceOpenPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "snmp_templates" || route.id === "snmp_template_detail") {
    return <SnmpConfigPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "mqtt_gateways" || route.id === "mqtt_templates" || route.id === "mqtt_template_detail" || route.id === "mqtt_devices") {
    return <MqttConfigPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "site_variable_log") {
    return <SiteVariableLogPage route={route} page={page} />;
  }
  if (route.id === "download_tasks") {
    return <DownloadTasksPage route={route} page={page} />;
  }
  if (route.id === "customized_report") {
    return <CustomizedReportPage route={route} page={page} />;
  }
  if (route.id === "flow_chat") {
    return <FlowChatPage route={route} page={page} />;
  }
  if (route.id === "smtp_settings") {
    return <SmtpSettingsPage route={route} page={page} />;
  }
  if (route.id === "report_task_log") {
    return <ReportTaskLogPage route={route} page={page} />;
  }
  if (route.id === "site_log_status") {
    return <SiteLogStatusPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "modbus_status") {
    return <ModbusStatusPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "snmp_status") {
    return <SnmpStatusPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "control_action_log") {
    return <ControlActionLogPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "site_offline_history") {
    return <SiteOfflineHistoryPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "webhook_log") {
    return <WebhookLogPage route={route} page={page} />;
  }
  if (route.id === "webhook_sender") {
    return <WebhookSenderPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "alarm_webhook") {
    return <AlarmWebhookPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "project_management") {
    return <ProjectManagementPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "redis_status") {
    return <RedisStatusPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "ping_test") {
    return <PingTestPage route={route} page={page} />;
  }
  if (route.id === "snmp_tester") {
    return <SnmpTesterPage route={route} page={page} />;
  }
  if (route.id === "api_monitor") {
    return <ApiMonitorPage route={route} page={page} />;
  }
  if (route.id === "alarm_rules") {
    return <AlarmRulesPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "offline_alarm_rules") {
    return (
      <OfflineAlarmRulesPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />
    );
  }
  if (route.id === "modbus_device_ui_config") {
    return <DeviceUiConfigEditor route={route} page={page} />;
  }
  if (route.id === "alarms_active") {
    return <ActiveAlarmsPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "alarms_history") {
    return <AlarmHistoryPage route={route} page={page} onRefresh={() => window.dispatchEvent(new Event("klt-rms-refresh-page"))} />;
  }
  if (route.id === "dashboard") {
    return <DashboardDesign route={route} page={page} />;
  }
  return <GenericDesign route={route} page={page} />;
}
